import type { Workflow } from "@/types/aiToolkit";
import type { WorkflowRun, WorkflowRunState, NodeOutput } from "@/types/workflowEngine";
import { createEmptyRunState } from "@/types/workflowEngine";
import { workflowRunsService } from "@/services/workflowRunsService";
import { organizationPoliciesService } from "@/services/organizationPoliciesService";
import { toolRegistry } from "@/services/toolRegistry";
import { getCurrentUserId } from "@/lib/supabase";
import { aiAgentsService } from "@/services/aiAgentsService";
import { aiWorkflowsService } from "@/services/aiWorkflowsService";
import {
  findEntryNode,
  findNode,
  resolveNextNodeIds,
  getPriorNodeId,
  resolveNodeInputs,
  findParallelJoin,
  branchTerminalsFeeding,
} from "./graphTraverser";
import { executeAgentNode, executeCompositeAgent } from "./agentNodeExecutor";
import { executeHttpRequestNode } from "./httpNodeExecutor";
import { decideLoop, maxVisitsFor } from "./nodePure";
import { getOutgoingEdges } from "./graphTraverser";
import { runCrew, crewResultToNodeOutput, type CrewDeps } from "./crewExecutor";
import type { CrewWorker } from "./crewPure";
import { aiService } from "@/services/aiService";
import { operatingLoopService } from "@/services/operatingLoopService";
import {
  executeKnowledgeNode,
  executeTransformNode,
  executeValidationNode,
  executeConfidenceGate,
  executeQualityGate,
  executeConditionNode,
  executeEntityResolutionNode,
  executeMergeNode,
} from "./nodeExecutors";
import { applyDataMapping } from "./graphTraverser";

export interface StartRunOptions {
  workflow: Workflow;
  versionId?: string | null;
  triggerType?: string;
  triggerPayload?: Record<string, unknown>;
  workspaceId?: string | null;
}

export interface RunProgressCallback {
  onNodeStart?: (nodeId: string, nodeType: string) => void;
  onNodeComplete?: (nodeId: string, output: unknown) => void;
  onPaused?: (reason: string, nodeId: string) => void;
}

class WorkflowEngineService {
  async publishVersion(workflow: Workflow, changelog?: string) {
    const workflowId = await aiWorkflowsService.ensureWorkflowRecord(workflow);
    if (!workflowId) return null;
    return workflowRunsService.publishVersion(workflowId, {
      nodes: workflow.nodes,
      edges: workflow.edges,
      entryNodeId: workflow.entryNodeId,
      trigger: workflow.trigger,
      variables: workflow.variables,
      changelog,
    });
  }

  async startRun(options: StartRunOptions, onProgress?: RunProgressCallback): Promise<WorkflowRun | null> {
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const policy = await organizationPoliciesService.getEffectivePolicy(options.workspaceId);
    const initialState = createEmptyRunState(options.triggerPayload);
    if (options.triggerPayload?.subject) initialState.metadata.source = String(options.triggerPayload.source ?? "manual");

    const dbWorkflowId = await aiWorkflowsService.ensureWorkflowRecord(options.workflow);
    if (!dbWorkflowId) return null;

    const run = await workflowRunsService.createRun({
      workflowId: dbWorkflowId,
      workflowVersionId: options.versionId ?? options.workflow.publishedVersionId ?? null,
      triggerType: options.triggerType ?? "manual",
      triggerPayload: options.triggerPayload ?? {},
      policySnapshot: policy as unknown as Record<string, unknown>,
      initialState,
    });
    if (!run) return null;

    await workflowRunsService.updateRun(run.id, { status: "running" });
    await this.executeFromNode(run, options.workflow, options.workflow.entryNodeId, onProgress);
    return workflowRunsService.getRun(run.id);
  }

  async resumeRun(runId: string, workflow: Workflow, fromNodeId?: string | null, onProgress?: RunProgressCallback): Promise<WorkflowRun | null> {
    const run = await workflowRunsService.getRun(runId);
    if (!run) return null;
    const startId = fromNodeId ?? run.currentNodeId ?? workflow.entryNodeId;
    await workflowRunsService.updateRun(runId, { status: "running" });
    await this.executeFromNode(run, workflow, startId, onProgress);
    return workflowRunsService.getRun(runId);
  }

  async pauseRun(runId: string, nodeId: string): Promise<void> {
    await workflowRunsService.updateRun(runId, { status: "paused", currentNodeId: nodeId });
    await workflowRunsService.createCheckpoint({ runId, nodeId, checkpointType: "pause" });
    await workflowRunsService.appendEvent({
      runId,
      nodeId,
      nodeType: null,
      eventType: "paused",
      actor: "user",
    });
  }

  async skipNode(runId: string, workflow: Workflow, nodeId: string): Promise<void> {
    const run = await workflowRunsService.getRun(runId);
    if (!run) return;
    await workflowRunsService.appendEvent({
      runId,
      nodeId,
      nodeType: findNode(workflow.nodes, nodeId)?.type ?? null,
      eventType: "skipped",
      actor: "user",
    });
    const nextIds = resolveNextNodeIds(findNode(workflow.nodes, nodeId)!, workflow.edges);
    if (nextIds[0]) await this.executeFromNode(run, workflow, nextIds[0]);
  }

  async approveHumanReviewAndContinue(
    runId: string,
    workflow: Workflow,
    humanReviewNodeId: string
  ): Promise<WorkflowRun | null> {
    const run = await workflowRunsService.getRun(runId);
    if (!run) return null;

    run.state.metadata.approvedHumanReview = {
      ...run.state.metadata.approvedHumanReview,
      [humanReviewNodeId]: true,
    };

    const nextIds = resolveNextNodeIds(
      findNode(workflow.nodes, humanReviewNodeId)!,
      workflow.edges
    );
    const nextNode = nextIds[0] ? findNode(workflow.nodes, nextIds[0]) : undefined;
    if (nextNode?.type === "tool" && nextNode.data.toolOperation) {
      run.state.metadata.approvedTools = {
        ...run.state.metadata.approvedTools,
        [nextNode.id]: true,
        [`${nextNode.id}:${nextNode.data.toolOperation}`]: true,
      };
    }

    delete run.state.metadata.pendingTool;
    await workflowRunsService.updateRun(runId, {
      status: "running",
      currentNodeId: humanReviewNodeId,
      state: run.state,
    });
    await workflowRunsService.appendEvent({
      runId,
      nodeId: humanReviewNodeId,
      nodeType: "human-review",
      eventType: "resumed",
      actor: "user",
      metadata: { approved: true },
    });
    await this.executeFromNode(run, workflow, humanReviewNodeId);
    return workflowRunsService.getRun(runId);
  }

  async approveToolAndContinue(
    runId: string,
    workflow: Workflow,
    toolNodeId: string
  ): Promise<WorkflowRun | null> {
    const run = await workflowRunsService.getRun(runId);
    if (!run) return null;
    const node = findNode(workflow.nodes, toolNodeId);
    if (!node?.data.toolOperation) return null;

    run.state.metadata.approvedTools = {
      ...run.state.metadata.approvedTools,
      [toolNodeId]: true,
      [`${toolNodeId}:${node.data.toolOperation}`]: true,
    };
    delete run.state.metadata.pendingTool;

    await workflowRunsService.updateRun(runId, {
      status: "running",
      currentNodeId: toolNodeId,
      state: run.state,
    });
    await this.executeFromNode(run, workflow, toolNodeId);
    return workflowRunsService.getRun(runId);
  }

  async dismissApproval(runId: string): Promise<WorkflowRun | null> {
    const run = await workflowRunsService.getRun(runId);
    if (!run) return null;
    run.state.metadata.dismissed = true;
    delete run.state.metadata.pendingTool;
    await workflowRunsService.updateRun(runId, {
      status: "completed",
      state: run.state,
      completedAt: new Date().toISOString(),
    });
    await workflowRunsService.appendEvent({
      runId,
      nodeId: run.currentNodeId,
      nodeType: "human-review",
      eventType: "skipped",
      actor: "user",
      metadata: { dismissed: true },
    });
    return workflowRunsService.getRun(runId);
  }

  async overrideNodeOutput(runId: string, nodeId: string, output: unknown): Promise<void> {
    const run = await workflowRunsService.getRun(runId);
    if (!run) return;
    run.state.nodeOutputs[nodeId] = {
      raw: typeof output === "string" ? output : JSON.stringify(output),
      parsed: output,
      editedByUser: true,
    };
    await workflowRunsService.updateRun(runId, { state: run.state });
    await workflowRunsService.appendEvent({
      runId,
      nodeId,
      nodeType: null,
      eventType: "edited",
      outputSnapshot: output,
      actor: "user",
    });
  }

  async forkToChat(runId: string, nodeId: string, chatThreadId: string): Promise<void> {
    const run = await workflowRunsService.getRun(runId);
    if (!run) return;
    const output = run.state.nodeOutputs[nodeId];
    await workflowRunsService.createCheckpoint({
      runId,
      nodeId,
      checkpointType: "chat_fork",
      outputOverride: output?.parsed ?? output?.raw,
      chatThreadId,
    });
    await workflowRunsService.updateRun(runId, { status: "waiting_chat", currentNodeId: nodeId });
    await workflowRunsService.appendEvent({
      runId,
      nodeId,
      nodeType: "agent",
      eventType: "chat_forked",
      metadata: { chatThreadId },
      actor: "user",
    });
  }

  async mergeChatCheckpoint(checkpointId: string, outputOverride: unknown, workflow: Workflow): Promise<WorkflowRun | null> {
    const runId = await this.getRunIdForCheckpoint(checkpointId);
    if (!runId) return null;
    const run = await workflowRunsService.getRun(runId);
    if (!run) return null;

    const { data } = await (await import("@/lib/supabase")).supabase
      .from("workflow_checkpoints")
      .select("*")
      .eq("id", checkpointId)
      .single();
    const nodeId = (data as { node_id?: string })?.node_id;
    if (!nodeId) return null;

    await workflowRunsService.resolveCheckpoint(checkpointId, outputOverride);
    run.state.nodeOutputs[nodeId] = {
      raw: typeof outputOverride === "string" ? outputOverride : JSON.stringify(outputOverride),
      parsed: outputOverride,
      editedByUser: true,
    };
    await workflowRunsService.updateRun(runId, { state: run.state, status: "running" });
    await workflowRunsService.appendEvent({
      runId,
      nodeId,
      nodeType: "agent",
      eventType: "chat_merged",
      outputSnapshot: outputOverride,
      actor: "user",
    });

    const nextIds = resolveNextNodeIds(findNode(workflow.nodes, nodeId)!, workflow.edges);
    if (nextIds[0]) await this.executeFromNode(run, workflow, nextIds[0]);
    return workflowRunsService.getRun(runId);
  }

  private async getRunIdForCheckpoint(checkpointId: string): Promise<string | null> {
    const { supabase } = await import("@/lib/supabase");
    const { data } = await supabase.from("workflow_checkpoints").select("run_id").eq("id", checkpointId).single();
    return (data as { run_id?: string })?.run_id ?? null;
  }

  private async executeFromNode(
    run: WorkflowRun,
    workflow: Workflow,
    startNodeId: string | null | undefined,
    onProgress?: RunProgressCallback,
    stopAtId?: string | null
  ): Promise<void> {
    let currentId: string | null | undefined = startNodeId;
    // 4A.4: loop-free graphs keep the legacy exactly-once visit rule; graphs
    // containing a loop node allow bounded revisits so back-edges can cycle.
    // maxSteps remains the global runaway backstop.
    const visitCounts = new Map<string, number>();
    const maxVisits = maxVisitsFor(workflow.nodes.some((n) => n.type === "loop"));
    const maxSteps = 100;
    let steps = 0;

    while (currentId && steps < maxSteps) {
      // 4A.6: a parallel branch stops AT the shared join so the join runs once
      // (in the main run), not once per branch.
      if (stopAtId && currentId === stopAtId) return;
      steps++;
      const seen = (visitCounts.get(currentId) ?? 0) + 1;
      if (seen > maxVisits) break;
      visitCounts.set(currentId, seen);

      const node = findNode(workflow.nodes, currentId) ?? findEntryNode(workflow.nodes, currentId);
      if (!node) break;

      if (
        node.type === "human-review" &&
        run.state.metadata.approvedHumanReview?.[node.id]
      ) {
        if (!run.state.nodeOutputs[node.id]) {
          run.state.nodeOutputs[node.id] = {
            raw: "approved",
            parsed: { status: "approved" },
          };
        }
        const nextIds = resolveNextNodeIds(node, workflow.edges);
        currentId = nextIds[0] ?? null;
        await workflowRunsService.updateRun(run.id, { currentNodeId: currentId, state: run.state });
        continue;
      }

      if (node.type === "tool") {
        const toolId = node.data.toolId!;
        const operation = node.data.toolOperation!;
        const def = toolRegistry.getDefinition(toolId);
        const opDef = def?.operations.find((o) => o.name === operation);
        const approvalKey = `${node.id}:${operation}`;
        const preApproved =
          run.state.metadata.approvedTools?.[approvalKey] ||
          run.state.metadata.approvedTools?.[node.id];

        if (opDef?.requiresApproval && !preApproved) {
          const input = applyDataMapping(node.data.toolInputMapping, {
            variables: run.state.variables,
            nodeOutputs: run.state.nodeOutputs,
          } as unknown as Record<string, unknown>) as Record<string, unknown>;
          run.state.metadata.pendingTool = {
            nodeId: node.id,
            toolId,
            operation,
            input,
          };
          await workflowRunsService.updateRun(run.id, {
            status: "waiting_approval",
            currentNodeId: node.id,
            state: run.state,
          });
          await workflowRunsService.createCheckpoint({
            runId: run.id,
            nodeId: node.id,
            checkpointType: "approval",
          });
          onProgress?.onPaused?.("tool_approval", node.id);
          return;
        }
      }

      // 4A.5: resolve this node's inputs from incoming-edge dataMapping (opt-in).
      // No-op for graphs whose edges carry no dataMapping (legacy behavior).
      const resolvedInputs = resolveNodeInputs(node.id, workflow.edges, {
        variables: run.state.variables,
        nodeOutputs: run.state.nodeOutputs,
        inputs: run.state.nodeInputs ?? {},
      } as unknown as Record<string, unknown>);
      if (Object.keys(resolvedInputs).length) {
        if (!run.state.nodeInputs) run.state.nodeInputs = {};
        run.state.nodeInputs[node.id] = resolvedInputs;
      }

      onProgress?.onNodeStart?.(node.id, node.type);
      const started = Date.now();

      await workflowRunsService.appendEvent({
        runId: run.id,
        nodeId: node.id,
        nodeType: node.type,
        eventType: "started",
        actor: "system",
      });

      try {
        const output = await this.executeNode(run, workflow, node);
        run.state.nodeOutputs[node.id] = output;

        await workflowRunsService.appendEvent({
          runId: run.id,
          nodeId: node.id,
          nodeType: node.type,
          eventType: "completed",
          outputSnapshot: output,
          confidence: output.confidence ?? null,
          quality: output.quality ?? null,
          validationResult: output.validation ?? null,
          durationMs: Date.now() - started,
          actor: "system",
        });

        onProgress?.onNodeComplete?.(node.id, output);

        if (node.type === "tool" && output.raw === "needs_permission") {
          await workflowRunsService.updateRun(run.id, {
            status: "waiting_approval",
            currentNodeId: node.id,
            state: run.state,
          });
          await workflowRunsService.createCheckpoint({
            runId: run.id,
            nodeId: node.id,
            checkpointType: "approval",
          });
          onProgress?.onPaused?.("tool_approval", node.id);
          return;
        }

        if (node.type === "human-review") {
          await workflowRunsService.updateRun(run.id, { status: "waiting_approval", currentNodeId: node.id, state: run.state });
          await workflowRunsService.createCheckpoint({ runId: run.id, nodeId: node.id, checkpointType: "approval" });
          onProgress?.onPaused?.("approval", node.id);
          return;
        }

        if (node.type === "parallel") {
          const branchTargets = resolveNextNodeIds(node, workflow.edges);
          const branchId = `branch-${node.id}-${Date.now()}`;
          await workflowRunsService.appendEvent({
            runId: run.id,
            nodeId: node.id,
            nodeType: node.type,
            eventType: "parallel_fork",
            parallelBranchId: branchId,
            metadata: { targets: branchTargets },
            actor: "system",
          });

          // 4A.6: resolve the join structurally (the node ALL branches converge
          // on), stopping each branch AT the join so it isn't run per-branch.
          const joinId = findParallelJoin(branchTargets, workflow.edges);

          const subRuns = await Promise.all(
            branchTargets.map(async (targetId) => {
              const subRun = { ...run, state: JSON.parse(JSON.stringify(run.state)) as WorkflowRunState };
              await this.executeFromNode(subRun, workflow, targetId, onProgress, joinId ?? undefined);
              return subRun.state;
            })
          );

          // Merge every branch's produced outputs back into the main run so
          // downstream nodes (and the join) see all branch data — not just the
          // first node of each branch (the old bug).
          for (const st of subRuns) {
            Object.assign(run.state.nodeOutputs, st.nodeOutputs);
            if (st.nodeInputs) run.state.nodeInputs = { ...(run.state.nodeInputs ?? {}), ...st.nodeInputs };
          }

          if (joinId) {
            // Collect each branch's TERMINAL output (the node feeding the join).
            const terminals = branchTerminalsFeeding(joinId, branchTargets, workflow.edges);
            run.state.parallel.mergeBuffers[joinId] = terminals
              .map((tid) => run.state.nodeOutputs[tid])
              .filter(Boolean) as typeof run.state.nodeOutputs[string][];
            currentId = joinId;
            continue;
          }
          // No reconvergence: independent fan-out — run each branch fully (done
          // above) and end this path.
          currentId = null;
          continue;
        }

        let branchKey: string | undefined;
        if (node.type === "confidence-gate") branchKey = output.raw;
        if (node.type === "quality-gate") branchKey = output.raw;
        if (node.type === "condition") branchKey = output.raw;
        if (node.type === "loop") branchKey = output.raw; // "loop" | "done" (4A.4)

        const nextIds = resolveNextNodeIds(node, workflow.edges, branchKey);
        currentId = nextIds[0] ?? null;

        if (node.type === "output" || !nextIds.length) {
          await workflowRunsService.updateRun(run.id, {
            status: "completed",
            currentNodeId: node.id,
            state: run.state,
            completedAt: new Date().toISOString(),
          });
          return;
        }

        await workflowRunsService.updateRun(run.id, { currentNodeId: currentId, state: run.state });
      } catch (e) {
        await workflowRunsService.updateRun(run.id, {
          status: "failed",
          error: e instanceof Error ? e.message : "Node execution failed",
          currentNodeId: node.id,
        });
        await workflowRunsService.appendEvent({
          runId: run.id,
          nodeId: node.id,
          nodeType: node.type,
          eventType: "failed",
          metadata: { error: e instanceof Error ? e.message : String(e) },
          actor: "system",
        });
        return;
      }
    }

    await workflowRunsService.updateRun(run.id, { status: "completed", state: run.state, completedAt: new Date().toISOString() });
  }

  /**
   * 4A.7 — crew node. Worker agents are the targets of this node's "worker"
   * edges. The manager (node.data.agentId, or a generic manager) decomposes the
   * goal and delegates to workers via the AI proxy until it finishes or the
   * round budget is exhausted. Workers run through the existing agent executor.
   */
  private async executeCrewNode(run: WorkflowRun, workflow: Workflow, node: Workflow["nodes"][0]): Promise<NodeOutput> {
    const workerEdges = getOutgoingEdges(workflow.edges, node.id).filter((e) => e.sourceHandle === "worker");
    const workerNodes = workerEdges
      .map((e) => findNode(workflow.nodes, e.target))
      .filter((n): n is Workflow["nodes"][0] => !!n && n.type === "agent");

    const workers: CrewWorker[] = workerNodes.map((w) => ({
      id: w.id,
      name: w.data.label || "Worker",
      description: w.data.description,
    }));
    const byId = new Map(workerNodes.map((w) => [w.id, w]));

    const goal =
      node.data.promptTemplate ||
      node.data.description ||
      String(run.state.variables.body ?? run.state.variables.content ?? run.state.nodeInputs?.[node.id]?.goal ?? "") ||
      node.data.label ||
      "Complete the workflow objective.";

    let managerInstructions: string | undefined;
    let managerModel = "openai/gpt-4o-mini";
    if (node.data.agentId) {
      const mgr = await aiAgentsService.getAgentById(node.data.agentId);
      if (mgr) {
        managerInstructions = mgr.customInstructions ?? undefined;
        if (mgr.workerProfileId) {
          const { workerProfilesService } = await import("@/services/workerProfilesService");
          const p = await workerProfilesService.getById(mgr.workerProfileId);
          if (p?.preferredModel) managerModel = p.preferredModel;
        }
      }
    }

    const deps: CrewDeps = {
      callManager: async (systemPrompt: string, goalText: string) => {
        const gated = await operatingLoopService.answerWithLoop({
          userId: await getCurrentUserId(),
          question: goalText,
          pickedModel: managerModel,
          app: "workflows",
          run: async ({ model, briefPrompt }) => {
            let text = "";
            for await (const chunk of aiService.streamChat({
              model,
              messages: [
                { role: "system", content: [briefPrompt, systemPrompt].filter(Boolean).join("\n\n") },
                { role: "user", content: goalText },
              ],
              maxTokens: 1200,
              temperature: 0.3,
              app: "workflows",
            })) {
              if (chunk.type === "error") throw new Error(chunk.error || "Manager step failed");
              if (chunk.type === "delta" && chunk.content) text += chunk.content;
            }
            return { text };
          },
        });
        return gated.text;
      },
      runWorker: async (workerId: string, task: string) => {
        const wNode = byId.get(workerId);
        if (!wNode) return `ERROR: unknown worker ${workerId}`;
        const out = await executeAgentNode(wNode, run.state, { userContent: task });
        run.state.nodeOutputs[wNode.id] = out; // expose worker output on the bus
        return out.raw;
      },
      onStep: (step) => {
        void workflowRunsService.appendEvent({
          runId: run.id,
          nodeId: node.id,
          nodeType: "crew",
          eventType: "completed",
          outputSnapshot: { raw: step.result, parsed: { worker: step.worker, task: step.task, round: step.round } },
          metadata: { crewWorker: step.worker, crewRound: step.round },
          actor: "system",
        });
      },
    };

    const result = await runCrew({ goal, workers, maxRounds: node.data.crewMaxRounds, managerInstructions }, deps);
    return crewResultToNodeOutput(result);
  }

  private async executeNode(run: WorkflowRun, workflow: Workflow, node: Workflow["nodes"][0]) {
    const userId = await getCurrentUserId();
    const userContent = String(run.state.variables.body ?? run.state.variables.content ?? run.state.variables.transcript ?? "");
    const priorNodeId = getPriorNodeId(workflow.edges, node.id);

    switch (node.type) {
      case "trigger":
        return { raw: "trigger_ok", parsed: run.triggerPayload };
      case "knowledge":
        return executeKnowledgeNode(node, run.state);
      case "agent": {
        const agent = node.data.agentId ? await aiAgentsService.getAgentById(node.data.agentId) : null;
        if (agent?.composedAgentIds?.length) {
          return executeCompositeAgent(node.data.agentId!, run.state, userContent, (subId, out) => {
            void workflowRunsService.appendEvent({
              runId: run.id,
              nodeId: subId,
              nodeType: "agent",
              eventType: "completed",
              outputSnapshot: out,
              metadata: { compositeParent: node.id },
              actor: "system",
            });
          });
        }
        return executeAgentNode(node, run.state, { userContent });
      }
      case "tool": {
        const toolId = node.data.toolId!;
        const operation = node.data.toolOperation!;
        const input = applyDataMapping(node.data.toolInputMapping, {
          variables: run.state.variables,
          nodeOutputs: run.state.nodeOutputs,
        } as unknown as Record<string, unknown>) as Record<string, unknown>;
        const result = await toolRegistry.invoke(toolId, operation, input, {
          userId: userId!,
          runId: run.id,
          nodeId: node.id,
          workflowId: run.workflowId,
        });
        if (!result.success && result.needsPermission) {
          run.state.metadata.pendingTool = { nodeId: node.id, toolId, operation, input };
          return {
            raw: "needs_permission",
            parsed: { needsPermission: true, error: result.error },
          };
        }
        if (!result.success) {
          return {
            raw: result.error ?? "Tool failed",
            parsed: { error: result.error },
          };
        }
        if (result.artifact && userId) {
          const art = await workflowRunsService.createArtifact({
            runId: run.id,
            nodeId: node.id,
            userId,
            artifactType: result.artifact.artifactType as "thread",
            threadId: result.artifact.threadId,
            kbDocumentId: result.artifact.kbDocumentId,
            clickupTaskId: result.artifact.clickupTaskId,
            gmailId: result.artifact.gmailId,
            externalId: result.artifact.externalId,
            title: result.artifact.title,
            url: result.artifact.url,
            metadata: result.artifact.metadata,
          });
          if (art) {
            await workflowRunsService.appendEvent({
              runId: run.id,
              nodeId: node.id,
              nodeType: "tool",
              eventType: "artifact_created",
              metadata: { artifactId: art.id },
              actor: "system",
            });
          }
        }
        return {
          raw: result.success ? JSON.stringify(result.output) : result.error ?? "Tool failed",
          parsed: result.output,
        };
      }
      case "transform":
        return executeTransformNode(node, run.state);
      case "validation":
        return executeValidationNode(node, run.state, priorNodeId);
      case "confidence-gate":
        return executeConfidenceGate(node, run.state, priorNodeId);
      case "quality-gate":
        return executeQualityGate(node, run.state, priorNodeId);
      case "condition":
        return executeConditionNode(node, run.state, priorNodeId);
      case "entity-resolution":
        return executeEntityResolutionNode(run.state);
      case "merge":
        return executeMergeNode(node, run.state);
      case "prompt": {
        const template = node.data.promptTemplate ?? "";
        return { raw: template, parsed: { template } };
      }
      case "delay":
        await new Promise((r) => setTimeout(r, node.data.delayMs ?? 1000));
        return { raw: "delay_complete" };
      case "crew": // 4A.7 — manager delegates to worker agents connected via "worker" edges
        return this.executeCrewNode(run, workflow, node);
      case "http-request": // 4A.3
        return executeHttpRequestNode(node, run.state);
      case "loop": { // 4A.4 — branch "loop" (body) while iteration <= loopCount, then "done"
        if (!run.state.metadata.loopCounters) run.state.metadata.loopCounters = {};
        const d = decideLoop(node.id, node.data.loopCount, run.state.metadata.loopCounters);
        return { raw: d.branch, parsed: { iteration: d.iteration, loopCount: node.data.loopCount ?? 1 } };
      }
      case "output":
        return { raw: JSON.stringify(run.state.nodeOutputs, null, 2), parsed: run.state.nodeOutputs };
      case "human-review":
        return { raw: "awaiting_approval", parsed: { status: "waiting" } };
      default:
        return { raw: `skipped:${node.type}` };
    }
  }
}

export const workflowEngineService = new WorkflowEngineService();
